import type { ComponentProps, ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { FieldLabelRow } from './field-label-row';
import { Combobox } from '../../custom/combobox';
import { cn } from '../../lib/utils';

export default function ComboboxTextField(
	props: ComponentProps<'select'> & {
		label: string;
		search: string;
		empty: string;
		options: { value: string; label: string }[];
		creatable?: boolean;
		createLabel?: (value: string) => string;
		onCreateNew?: (value: string) => void;
		variant?: 'default' | 'outline';
		children?: ReactNode;
		orientation?: 'vertical' | 'horizontal';
	},
) {
	const field = useFieldContext<string>();
	const isHorizontal = props.orientation === 'horizontal';
	const isOptional = useIsFieldOptional(field.name);

	return (
		<div className={props.className}>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{props.label && (
					<FieldLabelRow
						label={props.label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						className={cn('flex items-center justify-between', isHorizontal && 'w-36 shrink-0')}
					/>
				)}
				<div className={isHorizontal ? 'flex-1' : undefined}>
					<Combobox
						isError={hasFieldError(field.state.meta)}
						placeholder={props.label}
						commandEmpty={props.empty}
						searchPlaceholder={props.search}
						creatable={props.creatable}
						options={props.options}
						value={field.state.value}
						onChange={(val) => field.handleChange(val)}
						children={props.children}
					/>
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
